import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { MAX_IMAGE_SIZE_MB } from "../../helpers/homePageForm";
import { showError } from "../../utils/ alert";

interface ImageUploadFieldProps {
  /** Untuk scroll/focus saat validasi gagal (dipasang sebagai id="field-<id>"). */
  id: string;
  label: string;
  /** File baru, URL lama, atau null (kosong). */
  value: File | string | null;
  onChange: (file: File | null) => void;
  required?: boolean;
  error?: string;
  hint?: string;
  /** Preview lebih kecil, dipakai di dalam card item. */
  compact?: boolean;
}

export default function ImageUploadField({
  id,
  label,
  value,
  onChange,
  required,
  error,
  hint,
  compact = false,
}: ImageUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Object URL dibuat & dibersihkan di effect (aman untuk StrictMode dan
  // tidak bocor memori setiap kali komponen re-render).
  useEffect(() => {
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreviewImage(url);

      return () => URL.revokeObjectURL(url);
    }

    setPreviewImage(value || null);
  }, [value]);

  const handleUploadImage = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    // Reset supaya file yang sama bisa dipilih ulang setelah dihapus.
    if (inputRef.current) inputRef.current.value = "";

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showError("Invalid file", "Please select an image file.");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      showError(
        "File too large",
        `Image size must not exceed ${MAX_IMAGE_SIZE_MB} MB.`
      );
      return;
    }

    onChange(file);
  };

  const previewHeight = compact ? "h-[170px]" : "h-[260px]";

  return (
    <div>
      <label className="mb-2 block text-base font-semibold">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>

      <div className="relative">
        <div
          id={`field-${id}`}
          tabIndex={-1}
          className={`flex ${previewHeight} w-full items-center justify-center overflow-hidden rounded-3xl border bg-gray-100 outline-none ${
            error ? "border-red-400" : "border-gray-200"
          }`}>
          {previewImage ? (
            <img
              src={previewImage}
              alt={label}
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="text-center text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`mx-auto mb-3 ${compact ? "h-10 w-10" : "h-16 w-16"}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4-4 4 4 4-6 4 6"
                />
              </svg>

              <p>No Image</p>
            </div>
          )}
        </div>

        {previewImage && (
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label={`Remove ${label}`}
            className="absolute -right-3 -top-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-600 text-white shadow-lg hover:bg-red-700">
            ✕
          </button>
        )}
      </div>

      <label
        className={`mt-4 flex ${
          compact ? "h-11" : "h-14"
        } w-full cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-gray-300 text-green-600 transition hover:bg-green-50`}>
        {previewImage ? "Change Image" : "Upload Image"}
        <input
          ref={inputRef}
          type="file"
          hidden
          accept="image/*"
          onChange={handleUploadImage}
        />
      </label>

      {hint && !error && <p className="mt-1 text-sm text-gray-400">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
}
